import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { api, ApiError, setUnauthorizedHandler } from "@/api/client";
import type { LoginResponse, User } from "@/api/types";

type Status = "loading" | "authenticated" | "anonymous";

// CLI 43: el flow de login puede terminar en una sesión completa (status quo)
// o exigir un paso adicional. El caller usa el resultado para decidir si
// navegar a la app, mostrar el input del código TOTP, o redirigir al setup
// forzado.
export type LoginResult = "ok" | "requires_2fa" | "requires_2fa_setup";

export interface Verify2FAInput {
  code?: string;
  backup_code?: string;
}

interface AuthContextValue {
  user: User | null;
  status: Status;
  login: (username: string, password: string) => Promise<LoginResult>;
  verify2FA: (input: Verify2FAInput) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    try {
      const me = await api<User>("/api/v1/auth/me");
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus("anonymous");
      } else {
        setUser(null);
        setStatus("anonymous");
      }
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus("anonymous");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    // DEV-ONLY: mock user para preview local sin backend
    if (import.meta.env.DEV) {
      setUser({ id: "dev", username: "dev", role: "admin" } as User);
      setStatus("authenticated");
      return;
    }
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResult> => {
      // Backend devuelve:
      //   - 204 No Content → sesión completa (user sin 2FA o flag desactivado).
      //   - 200 + body LoginResponse → falta segundo factor o setup forzado.
      const body = await api<LoginResponse | undefined>("/api/v1/auth/login", {
        method: "POST",
        body: { username, password },
      });
      if (body === undefined) {
        await refresh();
        return "ok";
      }
      if (body.requires_2fa) return "requires_2fa";
      if (body.requires_2fa_setup) return "requires_2fa_setup";
      // Fallback defensivo: nunca debería caer acá si el backend responde
      // bien, pero asumimos sesión OK para no bloquear al operador.
      await refresh();
      return "ok";
    },
    [refresh],
  );

  const verify2FA = useCallback(
    async (input: Verify2FAInput) => {
      await api<void>("/api/v1/auth/totp/verify", {
        method: "POST",
        body: input,
      });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api<void>("/api/v1/auth/logout", { method: "POST" });
    } finally {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  const value = useMemo(
    () => ({ user, status, login, verify2FA, logout, refresh }),
    [user, status, login, verify2FA, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
